/*
Allow-list of read-only AI-artifact tRPC queries that are worth persisting for
the iPad offline cache (#2 in the funder-workspace data taxonomy).

These are generated server-side and read-only on the client, so they can be
dehydrated into the persisted React Query cache and served offline (with
`networkMode: "offlineFirst"`) without any write-queue. We deliberately exclude:
  - identity/auth (getCurrentUser*, getUserProfiles) — handled by the
    localStorage offline cache in offlineCache.ts,
  - the round aggregate (getAggregate) — has its own IndexedDB cache (PR1),
  - mutations and admin/job queries (listScheduledJobs, speculation/grant data).

Gating to the iPad shell happens at the persister wiring (TRPCProvider); this
module is just the pure key→persist decision so it can be unit-tested.
*/

import type { DehydratedState } from "@tanstack/react-query";
import { canonicalVideoKind } from "@lib/videoKinds";

/*
Changing a listed procedure's INPUT shape orphans existing offline entries.
Persisted entries are keyed by the React Query key, which includes the input,
so a renamed/added/removed input field means an iPad that cached the round
under the old shape can no longer read it. The cost is bounded — the entry is
unreachable, not wrong, and the next ONLINE visit repopulates it under the new
key — but an iPad that updates and then goes offline before revisiting the
round shows that surface's empty state instead of its cached content.

There is deliberately no migration path: re-keying a dehydrated entry means
recomputing the new input offline, which generally needs exactly the
round-aggregate lookups these inputs exist to avoid. If a future input change
can't tolerate one offline gap, bust the whole artifact cache instead of
hand-migrating entries.
*/

/** Read-only funder-workspace AI artifacts safe to persist for offline. */
export const PERSISTED_ARTIFACT_PROCEDURES: ReadonlySet<string> = new Set([
  "getFunderSummaries",
  "getFunderWorkspaceEnrichments",
  "getFunderRatingEstimates",
  "getProposalExtractions",
  // Pinned source-backed submissions are the primary proposal text for Builder rounds.
  "getProposalApplication",
  "getProposalApplicationSearch",
  "getMergedKeyPeople",
  "getKeyIndividualResearchReport",
  "getFactCheckResponses",
  "getProposalDeepResearch",
  "getRoundSummary",
  "getTrackSummary",
  "getProposalTranscriptClips",
  "getRecommenderTranscriptClips",
  "getRecommenderProposalExcerptLinks",
  // Backs the funder summary transcript-moment citations (Materials + Overview
  // panels resolve jump links from it); must be cached so those links survive
  // an iPad offline cold launch.
  "getRoundTranscriptMomentTargets",
  "getMeetings",
  "getMeetingTracks",
  "getTranscriptChapters",
  "getMvfQualityRating",
  "getMvfQualityResolution",
  "getMvfVisibility",
  // Native feature flags replaced PostHog's localStorage snapshot. Persist the
  // viewer-resolved set (input-less, plain booleans) so an iPad offline cold
  // start keeps last-known values instead of failing closed on every flag.
  "getEnabledFeatureFlags",
]);

/**
 * Extracts the tRPC procedure name from a React Query key. tRPC v11 keys are
 * `[["procedureName"], { input?, type }]`; the first element is the path array.
 * Returns null for anything that doesn't match that shape.
 */
export function artifactProcedureFromQueryKey(
  queryKey: unknown,
): string | null {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return null;
  const path = queryKey[0];
  if (Array.isArray(path)) {
    return path.length > 0 ? path.map(String).join(".") : null;
  }
  if (typeof path === "string") return path;
  return null;
}

/** True when the query is a read-only artifact worth persisting offline. */
export function shouldPersistArtifactQuery(queryKey: unknown): boolean {
  const procedure = artifactProcedureFromQueryKey(queryKey);
  return procedure != null && PERSISTED_ARTIFACT_PROCEDURES.has(procedure);
}

/** Only authoritative source/access revocations discard previously readable text. */
export function shouldEvictApplicationQuery(
  queryKey: unknown,
  error: unknown,
): boolean {
  const procedure = artifactProcedureFromQueryKey(queryKey);
  if (
    procedure !== "getProposalApplication" &&
    procedure !== "getProposalApplicationSearch"
  )
    return false;
  return isApplicationSourceRevoked(error);
}

/** Shared by cache eviction and readers: failures alone do not revoke access. */
export function isApplicationSourceRevoked(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("data" in error)) return false;
  const data = error.data;
  return (
    !!data &&
    typeof data === "object" &&
    "code" in data &&
    ["NOT_FOUND", "UNAUTHORIZED", "FORBIDDEN"].includes(String(data.code))
  );
}

const PERSISTED_CORPUS_COLLECTION_FIELDS = {
  getFunderSummaries: [
    "proposalNoteSummaries",
    "proposalExtractions",
    "recommenderSummaries",
    "recommenderProposalNoteSummaries",
  ],
  getFunderWorkspaceEnrichments: [
    "transcriptTrackSummaries",
    "sentiments",
    "factChecks",
    "keyPeopleExtractions",
    "noteScores",
    "proposalWeights",
    "transcripts",
  ],
} as const;

type PersistedQueryLike = {
  queryKey: unknown;
  state: { data?: unknown };
};

/**
 * Makes additive collection fields safe when restoring a corpus payload
 * written by an older client. Persisted query data is outside TypeScript's
 * runtime guarantees: without this compatibility boundary, adding a response
 * collection can make a warm first load crash before its background refresh.
 *
 * Only the two shared corpus payloads are normalized. Other artifact shapes
 * remain untouched so an incompatible schema change still fails visibly and
 * can be handled with an explicit cache-buster change.
 */
export function normalizePersistedCorpusQuery<T extends PersistedQueryLike>(
  query: T,
): T {
  const procedure = artifactProcedureFromQueryKey(query.queryKey);
  if (
    procedure !== "getFunderSummaries" &&
    procedure !== "getFunderWorkspaceEnrichments"
  ) {
    return query;
  }

  const data = query.state.data;
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return query;
  }

  const fields = PERSISTED_CORPUS_COLLECTION_FIELDS[procedure];
  let normalizedData: Record<string, unknown> | null = null;
  for (const field of fields) {
    if (!Array.isArray((data as Record<string, unknown>)[field])) {
      normalizedData ??= { ...data };
      normalizedData[field] = [];
    }
  }

  if (normalizedData == null) return query;
  return {
    ...query,
    state: { ...query.state, data: normalizedData },
  } as T;
}

// Persisted payload collections whose rows carry a video-kind string. The
// cache outlives builds by up to its 30-day max age, so a snapshot written
// before a kind RENAME still holds the old enum labels; every kind predicate
// treats an unrecognized label as an unknown kind (dropped from
// surface-filtered strips, sorted last, raw enum as its group heading) until
// the first online refresh. The iPhone's persisted catalog already maps old
// labels forward on hydration (see mobileVideoStore's coerceMobileVideoType);
// this is the same repair for the webapp's offline cache.
const PERSISTED_VIDEO_KIND_COLLECTIONS = {
  getMeetings: "meetings",
  getProposalTranscriptClips: "excerpts",
  getRecommenderTranscriptClips: "excerpts",
  // Moment targets carry `type` and the citation resolver branches on it —
  // a cached row holding a renamed label would miss the rail-only branch.
  getRoundTranscriptMomentTargets: "targets",
} as const;

/** Map renamed video-kind labels forward in a persisted payload's rows. */
export function normalizePersistedVideoKinds<T extends PersistedQueryLike>(
  query: T,
): T {
  const procedure = artifactProcedureFromQueryKey(query.queryKey);
  if (procedure == null || !(procedure in PERSISTED_VIDEO_KIND_COLLECTIONS)) {
    return query;
  }
  const field =
    PERSISTED_VIDEO_KIND_COLLECTIONS[
      procedure as keyof typeof PERSISTED_VIDEO_KIND_COLLECTIONS
    ];

  const data = query.state.data;
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return query;
  }
  const rows = (data as Record<string, unknown>)[field];
  if (!Array.isArray(rows)) return query;

  let changed = false;
  const mapped = rows.map((row) => {
    if (row == null || typeof row !== "object") return row;
    const record = row as Record<string, unknown>;
    let next = row;
    const type = record.type;
    if (typeof type === "string") {
      const canonical = canonicalVideoKind(type);
      if (canonical !== type) {
        changed = true;
        next = { ...record, type: canonical };
      }
    }
    // Lineage fields postdate some snapshots (they ship in every fresh
    // response, but a snapshot written by a pre-lineage build lacks the
    // keys). Default them to null — explicitly unlinked — which is exactly
    // what the pre-lineage server would have said: the excerpt files under
    // "Unlinked excerpts" until the first online refresh restores its link,
    // instead of crashing or being silently dropped from the strips.
    if (procedure === "getMeetings" && !("sourceVideoId" in record)) {
      changed = true;
      next = {
        ...(next as Record<string, unknown>),
        sourceVideoId: null,
        sourceVideoType: null,
      };
    }
    return next;
  });

  if (!changed) return query;
  return {
    ...query,
    state: { ...query.state, data: { ...data, [field]: mapped } },
  } as T;
}

/**
 * Drop a cached `getRoundTranscriptMomentTargets` payload that predates clip
 * citations.
 *
 * Unlike the repairs above this can't patch the rows — the whole clip family
 * is simply absent from a pre-migration snapshot, and the cache buster keys on
 * the round AGGREGATE schema, which doesn't move when a tRPC response shape
 * changes. Serving one beside freshly fetched summaries makes every citation
 * in them inert (each id resolves to no target), for up to the cache's 30-day
 * max age. Every row the server returns now carries `type`, so rows without it
 * are definitionally pre-migration: discard the data and let the query refetch
 * rather than render a summary whose evidence links are all dead.
 */
function dropStaleMomentTargets<T extends PersistedQueryLike>(query: T): T {
  if (
    artifactProcedureFromQueryKey(query.queryKey) !==
    "getRoundTranscriptMomentTargets"
  ) {
    return query;
  }
  const targets = (query.state.data as { targets?: unknown } | undefined)
    ?.targets;
  if (!Array.isArray(targets) || targets.length === 0) return query;
  const predatesClipTargets = targets.every(
    (row) =>
      row == null || typeof row !== "object" || !("type" in (row as object)),
  );
  if (!predatesClipTargets) return query;
  return { ...query, state: { ...query.state, data: undefined } } as T;
}

/** Normalize every query in an iPad dehydrated artifact snapshot. */
export function normalizePersistedArtifactState(
  state: DehydratedState,
): DehydratedState {
  return {
    ...state,
    queries: state.queries.map((query) =>
      dropStaleMomentTargets(
        normalizePersistedVideoKinds(normalizePersistedCorpusQuery(query)),
      ),
    ),
  };
}
